import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Page } from 'react-pdf';
import Loader from '@components/Loader';
import PdfPageDisplayCanvas from '@pages/MediaViewer/AssetViewer/PdfCard/components/PdfPageDisplayCanvas';
import { hasRenderedTextLayerText } from '@pages/MediaViewer/AssetViewer/PdfCard/utils';

const pageLoader = (
  <div className="media_viewer_pdf-mobile-loader">
    <Loader spinSize={40} isSpinning={true} />
  </div>
);

type PdfPageProps = {
  pageNumber: number;
  pageWidth?: number;
  pageHeight?: number;
  zoomFactor: number;
  turnoverDegree: number;
  isActive: boolean;
  /** Drawing is in progress on another page */
  isDrawingDisabled?: boolean;
  isMainCanvasVisible: boolean;
  /** A comment is viewed (not edited): other pins stay tappable */
  isViewingComment?: boolean;
  isFirstPage: boolean;
  shouldRender: boolean;
  pageCanvasRef?: React.RefObject<HTMLCanvasElement>;
  onRegisterPageRoot: (page: number, element: HTMLDivElement | null) => void;
  onFirstPageLoadSuccess: (pdfPage: {
    originalWidth: number;
    originalHeight: number;
  }) => void;
  onPageLoadSuccess?: (pageNumber: number) => void;
  onPageTextContent?: (pageNumber: number, hasText: boolean) => void;
  /** Lets a comment be placed on the page while it is not the active one */
  onActivatePage?: (pageNumber: number) => void;
};

function PdfPage({
  pageNumber,
  pageWidth,
  pageHeight,
  zoomFactor,
  turnoverDegree,
  isActive,
  isDrawingDisabled = false,
  isMainCanvasVisible,
  isViewingComment = false,
  isFirstPage,
  shouldRender,
  pageCanvasRef,
  onRegisterPageRoot,
  onFirstPageLoadSuccess,
  onPageLoadSuccess,
  onPageTextContent,
  onActivatePage
}: PdfPageProps) {
  const shouldRenderPage = shouldRender || isActive;
  // The main canvas over the active page shows the comment being drawn or
  // edited, or only the viewed one, over the other pins
  const isDisplayCanvasInteractive = !isMainCanvasVisible || isViewingComment;
  const showDisplayCanvas =
    (shouldRender || isActive) && (!isActive || isDisplayCanvasInteractive);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [pageAspectRatio, setPageAspectRatio] = useState<number | null>(null);
  const renderedPageSize = useMemo(
    () => ({
      width: pageWidth,
      height:
        pageWidth && pageAspectRatio ? pageWidth * pageAspectRatio : pageHeight
    }),
    [pageWidth, pageHeight, pageAspectRatio]
  );

  const assignRootRef = useCallback(
    (element: HTMLDivElement | null) => {
      rootRef.current = element;
      onRegisterPageRoot(pageNumber, element);
    },
    [onRegisterPageRoot, pageNumber]
  );

  return (
    <div
      ref={assignRootRef}
      className={classNames('media_viewer_pdf-mobile-page', {
        'b-active': isActive,
        'b-drawing-disabled': isDrawingDisabled
      })}
      data-page-number={pageNumber}
      style={{ minHeight: renderedPageSize.height }}
    >
      <div
        className="media_viewer_pdf-inner"
        style={{
          transform: `rotate(${turnoverDegree}deg)`,
          width: renderedPageSize.width,
          height: renderedPageSize.height
        }}
      >
        {shouldRenderPage ? (
          <Page
            width={pageWidth}
            scale={1}
            pageNumber={pageNumber}
            className="media_viewer_pdf-page"
            canvasRef={isActive ? pageCanvasRef : undefined}
            renderAnnotationLayer={false}
            onLoadSuccess={(pdfPage) => {
              const viewport = pdfPage.getViewport({
                scale: 1,
                rotation: pdfPage.rotate
              });
              setPageAspectRatio(viewport.height / viewport.width);
              onPageLoadSuccess?.(pageNumber);
              if (isFirstPage) {
                onFirstPageLoadSuccess({
                  originalWidth: viewport.width,
                  originalHeight: viewport.height
                });
              }
            }}
            onRenderTextLayerSuccess={() => {
              onPageTextContent?.(
                pageNumber,
                hasRenderedTextLayerText(rootRef.current)
              );
            }}
            loading={pageLoader}
          />
        ) : null}
        <PdfPageDisplayCanvas
          pageNumber={pageNumber}
          pageWidth={renderedPageSize.width}
          pageHeight={renderedPageSize.height}
          zoomFactor={zoomFactor}
          turnoverDegree={turnoverDegree}
          visible={showDisplayCanvas}
          interactive={isDisplayCanvasInteractive}
          isActive={isActive}
          onActivatePage={onActivatePage}
        />
      </div>
    </div>
  );
}

export default memo(PdfPage);
